import apiClient, { API_BASE } from './apiClient';

// finding D-01(b): apiClient never populates `defaults.headers.common` — the
// auth header is injected per-request by a request INTERCEPTOR (see
// apiClient.js), so reading `apiClient.defaults.headers.common['Authorization']`
// was always undefined and these two raw `fetch()` calls (which can't use
// apiClient directly — they need the streaming/blob response body, not axios's
// parsed one) silently sent no Authorization header at all. Read the token the
// same way the interceptor does.
function getAuthHeaderValue() {
    const raw = localStorage.getItem('auth_token');
    return raw ? `Bearer ${raw}` : null;
}

const contentService = {
    startGeneration: (data) =>
        apiClient.post(`/content/generate`, data),

    getGeneration: (id) => apiClient.get(`/content/generation/${id}`),

    // finding D-06: lightweight {progress, status, current_step} for polling —
    // the full document above pulls scripts/research/data_registry too, which
    // a 2-3s poll loop has no use for.
    getGenerationStatus: (id) => apiClient.get(`/content/generation/${id}/status`),

    getGenerations: (page = 1, limit = 50) => apiClient.get(`/content/generations?page=${page}&limit=${limit}`),

    getUserLocation: () => apiClient.get('/content/location'),

    cancelGeneration: (id) => apiClient.post(`/content/generation/${id}/cancel`),

    reviewTopics: (id, discardedTopicIds, selectedTopicIds) =>
        apiClient.post(`/content/generation/${id}/review`, {
            discarded_topic_ids: discardedTopicIds,
            selected_topic_ids: selectedTopicIds
        }),

    replaceTopic: (id, data) =>
        apiClient.post(`/content/generation/${id}/replace-topic`, data),

    replaceTitle: (id) =>
        apiClient.post(`/content/generation/${id}/replace-title`),

    deleteGeneration: (id) => apiClient.delete(`/content/generation/${id}`),

    clearAllGenerations: () => apiClient.delete(`/content/generations/clear`),

    getTtsScripts: (generationId, index) =>
        apiClient.get(`/content/script/tts/${generationId}/${index}`),

    getScriptContent: (generationId, index) =>
        apiClient.get(`/content/script/${generationId}/${index}`),

    rewriteScript: async (payload, onProgress) => {
        const token = getAuthHeaderValue();
        const response = await fetch(`${API_BASE}/endocore/rewrite`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: token } : {}),
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || err.detail || 'Failed to rewrite script');
        }

        // finding D-08: `response.body` can be null (e.g. some fetch polyfills,
        // or a response consumed elsewhere) — guard before getReader() rather
        // than crashing with a less obvious TypeError.
        if (!response.body) {
            throw new Error('No response body received from rewrite stream');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let done = false;
        let finalData = null;

        while (!done) {
            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            if (value) {
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        // finding D-08: parse first, THEN dispatch outside the
                        // try/catch. Previously the `throw` for a server-sent
                        // 'error' status lived inside the same try that catches
                        // JSON.parse failures, so its own catch intercepted it
                        // and logged the misleading "Failed to parse stream
                        // chunk" — finalData stayed null and the caller's
                        // destructuring threw an unrelated TypeError instead of
                        // seeing the server's actual error message.
                        let data;
                        try {
                            data = JSON.parse(line.substring(6));
                        } catch (e) {
                            console.warn('Failed to parse stream chunk:', line);
                            continue;
                        }
                        if (['gatekeeper', 'structuring', 'polishing', 'validating'].includes(data.status)) {
                            if (onProgress) onProgress(data.status);
                        } else if (data.status === 'complete') {
                            finalData = data;
                        } else if (data.status === 'error') {
                            throw new Error(data.message || 'Stream error');
                        }
                    }
                }
            }
        }

        return { data: finalData };
    },

    updateScript: (id, payload) =>
        apiClient.put(`/content/generation/${id}/script`, payload),

    downloadGenerationZip: async (generationId, scriptIndex = null) => {
        const token = getAuthHeaderValue();
        let url = `${API_BASE}/content/generation/${generationId}/download-all`;
        if (scriptIndex !== null && scriptIndex !== undefined) {
            url += `?script_index=${scriptIndex}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                ...(token ? { Authorization: token } : {}),
            },
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || `Export failed: ${response.status}`);
        }

        const contentDisp = response.headers.get('content-disposition');
        let filename = scriptIndex !== null && scriptIndex !== undefined
            ? `script_${scriptIndex + 1}.zip`
            : `generation_${generationId.slice(0, 8)}.zip`;
        if (contentDisp && contentDisp.includes('filename=')) {
            const parts = contentDisp.split('filename=');
            if (parts[1]) {
                filename = parts[1].replace(/"/g, '').trim();
            }
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
    },
    // ── Topic Cache (Generator Memory) ──────────────────────────────────────
    getTopicCache: () => apiClient.get('/content/cache'),

    deleteCacheEntry: (id) => apiClient.delete(`/content/cache/${id}`),

    clearTopicCache: () => apiClient.delete('/content/cache/all'),

    // ── Intent Switchblade — Custom Theme Triage ────────────────────────────
    triageTheme: (theme) => apiClient.post('/content/triage-theme', { theme }),
    // ────────────────────────────────────────────────────────────────────────
};

export default contentService;
